import { React, Component, classNames } from '@digsup/core-ui';
import PropTypes from 'prop-types';
import { styles } from './Input.scss';

const cx = classNames.bind(styles);

export class InputComponent extends Component {
  renderLabeledInput(children) {
    const { addonBefore, addonAfter, size } = this.props;

    // No wrapper when there is no addons
    if (!addonBefore && !addonAfter) {
      return children;
    }

    const addonClasses = cx('c-input-group__addon');

    const addonBeforeNode = addonBefore ? (
      <span className={addonClasses}>{addonBefore}</span>
    ) : null;
    const addonAfterNode = addonAfter ? (
      <span className={addonClasses}>{addonAfter}</span>
    ) : null;

    const wrapperClasses = cx('c-input-group', {
      [`c-input-group--${size}`]: size,
    });

    return (
      <div className={wrapperClasses}>
        {addonBeforeNode}
        {React.cloneElement(children)}
        {addonAfterNode}
      </div>
    );
  }

  renderLabeledIcon(children) {
    const { prefix, suffix, size, isReadOnly } = this.props;

    // No wrapper when there is no inner nodes
    if (!prefix && !suffix) {
      return children;
    }

    const wrapperClasses = cx('c-input-affix', {
      [`c-input-affix--${size}`]: size,
    });
    const prefixClasses = cx('c-input-affix__prefix', {
      'c-input-affix__prefix--read-only': isReadOnly,
    });
    const suffixClasses = cx('c-input-affix__suffix', {
      'c-input-affix__suffix--read-only': isReadOnly,
    });

    const prefixNode = prefix ? (
      <span className={prefixClasses}>{prefix}</span>
    ) : null;

    const suffixNode = suffix ? (
      <span className={suffixClasses}>{suffix}</span>
    ) : null;

    return (
      <div className={wrapperClasses}>
        {prefixNode}
        {React.cloneElement(children)}
        {suffixNode}
      </div>
    );
  }

  renderInput() {
    const {
      disabled,
      placeholder,
      defaultValue,
      value,
      size,
      hasError,
      onChange,
      type,
      className,
      // eslint-disable-next-line no-unused-vars
      addonAfter,
      // eslint-disable-next-line no-unused-vars
      error,
      // eslint-disable-next-line no-unused-vars
      enterButton,
      // eslint-disable-next-line no-unused-vars
      onSearch,
      // eslint-disable-next-line no-unused-vars
      visibilityToggle,
      forwarderRef,
      isInlineEditable,
      isReadOnly,
      ...props
    } = this.props;

    const classes = cx(
      'c-input',
      {
        'c-input--disabled': disabled,
        [`c-input--${size}`]: size,
        [`c-input--error`]: hasError,
        'c-input--read-only': isReadOnly,
      },
      className,
    );

    const inputElement = this.renderLabeledIcon(
      <input
        ref={forwarderRef}
        type={type}
        className={classes}
        disabled={disabled}
        placeholder={placeholder}
        defaultValue={defaultValue}
        value={value}
        onChange={onChange}
        readOnly={isReadOnly}
        {...props}
      />,
    );

    if (isInlineEditable) {
      const wrapperClasses = cx('c-input-inline-editable-wrapper', {
        'c-input-inline-editable-wrapper--disabled': disabled,
        [`c-input-inline-editable-wrapper--${size}`]: size,
        'c-input-inline-editable-wrapper--error': hasError,
        'c-input-inline-editable-wrapper--read-only': isReadOnly,
      });

      return (
        <div className={wrapperClasses} title="Click to edit">
          {inputElement}
        </div>
      );
    }

    return inputElement;
  }

  render() {
    return this.renderLabeledInput(this.renderInput());
  }
}

export const Input = React.forwardRef((props, ref) => (
  <InputComponent {...props} forwarderRef={ref} />
));

Input.propTypes = {
  disabled: PropTypes.bool,
  hasError: PropTypes.bool,

  type: PropTypes.string,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.string,
  value: PropTypes.string,

  /**
   * Node displayed inside (on the left side of) the input field
   */
  prefix: PropTypes.node,
  /**
   * Node displayed inside (on the right side of) the input field
   */
  suffix: PropTypes.node,
  /**
   * Node displayed after (on the right side of) the input field
   */
  addonAfter: PropTypes.node,
  /**
   * Node displayed before (on the left side of) the input field
   */
  addonBefore: PropTypes.node,
  size: PropTypes.oneOf(['small', 'medium', 'large']),

  /**
   * callback when user input
   */
  onChange: PropTypes.func,
  /**
   * Extend components classNames
   */
  className: PropTypes.string,
  /**
   * Inline editable mode
   */
  isInlineEditable: PropTypes.bool,

  /**
   * Prevents the user from changing the value of the field
   */
  isReadOnly: PropTypes.bool,
};

Input.defaultProps = {
  type: 'text',
  disabled: false,
  hasError: false,
  size: 'medium',
  onChange: null,
};
